/**
 * Авторские индикаторы на накопленных рядах сервера.
 *
 *   - «Позиционирование толпы» — какая доля счетов стоит в длинных и как
 *     расставлены крупные трейдеры;
 *   - «Оценка Market Intelligence» — как менялась оценка и из чего она
 *     складывалась.
 *
 * Оба ряда реже графика: позиционирование — пять минут у главных монет и
 * час у остальных, оценка — пять минут. На свече берётся последнее
 * известное к её открытию значение, а если его давно не было — пусто:
 * протянуть вчерашнее через дыру значило бы нарисовать то, чего не было.
 */

import { timeframeMs, type Candle, type DerivativePoint } from '@specter/types';
import type { ReadoutRow } from './overlays.js';
import { situationRows, type Situation } from './explain.js';
import type { IndicatorContext, IndicatorDefinition, IndicatorParams, IndicatorPoint } from './types.js';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

const COLORS = {
  accounts: '#6E8BFF',
  top: '#E0A458',
  up: '#16C784',
  down: '#EA3943',
  grey: '#79818F',
  parts: ['#5AC8D8', '#3FBF87', '#F0616F', '#9B8CE0', '#E0A458', '#FF8A5B', '#C7CDD8'],
} as const;

/**
 * Последнее известное к открытию свечи значение.
 *
 * `maxAge` — сколько значение живёт без обновления. Дальше — пусто.
 */
export function asOf(
  candles: readonly Candle[],
  points: readonly DerivativePoint[],
  maxAge: number,
): Array<DerivativePoint | null> {
  const out: Array<DerivativePoint | null> = [];
  let index = -1;
  for (const candle of candles) {
    while (index + 1 < points.length && points[index + 1]!.at <= candle.time) index += 1;
    const point = index >= 0 ? points[index]! : null;
    out.push(point && candle.time - point.at <= maxAge ? point : null);
  }
  return out;
}

/** Шаг ряда — по самим точкам: пять минут или час. */
function stepOf(points: readonly DerivativePoint[]): number {
  let best = Number.POSITIVE_INFINITY;
  for (let i = 1; i < points.length && i < 50; i += 1) {
    const gap = points[i]!.at - points[i - 1]!.at;
    if (gap > 0 && gap < best) best = gap;
  }
  return Number.isFinite(best) ? best : HOUR;
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function bool(p: IndicatorParams, key: string, fallback: boolean): boolean {
  return typeof p[key] === 'boolean' ? (p[key] as boolean) : fallback;
}

function num(p: IndicatorParams, key: string, fallback: number): number {
  return typeof p[key] === 'number' && Number.isFinite(p[key]) ? (p[key] as number) : fallback;
}

/* ------------------------------------------------------ позиционирование */

/** Сколько живёт точка: три шага ряда, но не меньше бара графика. */
function ageFor(points: readonly DerivativePoint[], candles: readonly Candle[]): number {
  const bar = candles.length > 1 ? candles[1]!.time - candles[0]!.time : HOUR;
  return Math.max(3 * stepOf(points), bar);
}

function crowdWord(share: number, high: number): { text: string; tone: 'bull' | 'bear' | 'muted' } {
  if (share >= high) return { text: 'толпа в росте', tone: 'bull' };
  if (share <= 1 - high) return { text: 'толпа в падении', tone: 'bear' };
  return { text: 'без перекоса', tone: 'muted' };
}

export const CROWD_POSITIONING: IndicatorDefinition = {
  id: 'crowd-positioning',
  name: 'Позиционирование толпы',
  description:
    'Какая доля счетов на бирже держит ставки на рост и как расставлены крупные трейдеры. ' +
    'Когда толпа сильно перекошена в одну сторону, а крупные — нет, рынок часто идёт против толпы.',
  author: 'specter',
  placement: 'pane',
  derivatives: ['longAccounts', 'topPositions'],
  params: [
    {
      key: 'top',
      label: 'Крупные трейдеры',
      hint: 'Вторая линия — доля объёма позиций в длинных у крупных трейдеров. Есть только у Binance.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'high',
      label: 'Перекос от, %',
      hint: 'С какой доли считать, что толпа перекошена. 65% — две трети счетов в одной стороне.',
      type: 'number',
      default: 65,
      min: 51,
      max: 90,
      step: 1,
    },
    {
      key: 'table',
      label: 'Табло',
      hint: 'Короткая сводка словами в углу графика: что сейчас и насколько это заметно.',
      type: 'boolean',
      default: true,
    },
  ],
  outputs: [
    { key: 'accounts', label: 'Счета в длинных, %', type: 'line', defaultColor: COLORS.accounts, lineWidth: 2 },
    { key: 'top', label: 'Крупные в длинных, %', type: 'line', defaultColor: COLORS.top, lineWidth: 2 },
  ],
  scale: {
    levels: [{ value: 50, color: COLORS.grey, dashed: true }],
  },
  warmup: () => 0,
  label: () => 'Позиционирование толпы',
  calc: (candles, p, context) => {
    const series = (kind: 'longAccounts' | 'topPositions'): IndicatorPoint[] => {
      const points = context.derivative(kind);
      return asOf(candles, points, ageFor(points, candles)).map((point, i) => ({
        time: candles[i]!.time,
        value: point === null ? null : Math.round(point.value * 1000) / 10,
      }));
    };
    return {
      accounts: series('longAccounts'),
      // Выключенная линия — пустые значения: реестр ждёт точку на каждой свече.
      top: bool(p, 'top', true) ? series('topPositions') : candles.map((candle) => ({ time: candle.time, value: null })),
    };
  },
  readout: (candles, p, context) => {
    if (!bool(p, 'table', true) || candles.length === 0) return [];
    if (!context.derivativesReady) return [{ label: 'Данные', value: 'едут…', tone: 'muted' }];
    const accounts = context.derivative('longAccounts');
    const last = asOf(candles.slice(-1), accounts, ageFor(accounts, candles))[0];
    if (!last) {
      return [{ label: 'Данные', value: 'по этой монете ещё не собраны', tone: 'muted' }];
    }
    const high = num(p, 'high', 65) / 100;
    const word = crowdWord(last.value, high);
    const situation: Situation =
      word.tone === 'bull'
        ? {
            title: 'Толпа в лонгах',
            tone: 'warn',
            text: 'Большинство счетов ставит на рост. На крайностях толпа часто ошибается: когда все уже купили, покупать некому, и цена нередко идёт против них.',
          }
        : word.tone === 'bear'
          ? {
              title: 'Толпа в шортах',
              tone: 'warn',
              text: 'Большинство счетов ставит на падение. На крайностях толпа часто ошибается: когда все уже продали, продавать некому, и цена нередко идёт против них.',
            }
          : { title: 'Без перекоса', tone: 'muted', text: 'Счета поделились почти поровну — толпа сама не знает, куда пойдёт цена.' };
    const rows: ReadoutRow[] = [
      ...situationRows(situation),
      { label: 'Счета в длинных', value: percent(last.value), meter: last.value * 100 },
    ];
    const tops = context.derivative('topPositions');
    const top = bool(p, 'top', true) ? asOf(candles.slice(-1), tops, ageFor(tops, candles))[0] : null;
    if (top) {
      rows.push({ label: 'Крупные в длинных', value: percent(top.value), meter: top.value * 100 });
      const gap = last.value - top.value;
      if (Math.abs(gap) >= 0.1) {
        rows.push({
          label: 'Расхождение',
          value: gap > 0 ? 'толпа смелее крупных в росте' : 'крупные смелее толпы в росте',
          tone: gap > 0 ? 'down' : 'up',
        });
      }
    }
    if (stepOf(accounts) >= HOUR && candles.length > 1 && candles[1]!.time - candles[0]!.time < HOUR) {
      rows.push({ label: 'Шаг данных', value: 'час — реже графика', tone: 'muted' });
    }
    return rows;
  },
};

/* --------------------------------------------------------------- оценка */

const PARTS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'price', label: 'Цена' },
  { key: 'spot', label: 'Спот' },
  { key: 'futures', label: 'Контракты' },
  { key: 'openInterest', label: 'Интерес' },
  { key: 'volume', label: 'Объём' },
  // Не из пульса, а из факторов снимка: без них сумма не сходилась с итогом.
  { key: 'liquidations', label: 'Ликвидации' },
  { key: 'funding', label: 'Финансирование' },
];

/** Порог, с которого Market Intelligence называет сторону. */
const BIAS_AT = 20;

function signed(value: number): string {
  const rounded = Math.round(value);
  return rounded > 0 ? `+${rounded}` : rounded < 0 ? `−${Math.abs(rounded)}` : '0';
}

/**
 * Для каждой свечи — последний снимок внутри неё.
 *
 * Раньше бралась оценка на открытие свечи, то есть на конец предыдущей:
 * табло часовой свечи показывало состояние часовой давности, а вся
 * история стояла на свечу правее, будто оценка реагирует на цену с
 * опозданием. Теперь закрытая свеча несёт оценку своего конца, а
 * формирующаяся — самую свежую и меняется вместе с ней, как любой
 * индикатор до закрытия.
 *
 * Снимок пишется раз в пять минут. На свечах короче пятнадцати минут
 * своего снимка внутри может не быть — тогда берётся последний не
 * старше пятнадцати минут; дальше это уже дыра, а не «сейчас».
 */
export function latestWithin(
  candles: readonly Candle[],
  points: readonly DerivativePoint[],
  step: number,
): Array<DerivativePoint | null> {
  const reach = Math.max(step, 15 * MINUTE);
  const out: Array<DerivativePoint | null> = [];
  let index = -1;
  for (const candle of candles) {
    const end = candle.time + step;
    while (index + 1 < points.length && points[index + 1]!.at < end) index += 1;
    const point = index >= 0 ? points[index]! : null;
    out.push(point && point.at >= end - reach ? point : null);
  }
  return out;
}

function scorePoints(candles: readonly Candle[], context: IndicatorContext): Array<DerivativePoint | null> {
  return latestWithin(candles, context.derivative('miScore'), timeframeMs(context.timeframe));
}

export const MI_SCORE: IndicatorDefinition = {
  id: 'mi-score',
  name: 'Оценка Market Intelligence',
  description:
    'Как менялась общая оценка рынка из Market Intelligence: выше +20 — перевес покупателей, ниже −20 — продавцов. ' +
    'Можно показать, из чего она складывалась: цена, спот, контракты, интерес, объём, ликвидации, финансирование. История есть с 17 сентября 2026 по BTC, ETH и избранному.',
  author: 'specter',
  placement: 'pane',
  derivatives: ['miScore'],
  params: [
    {
      key: 'parts',
      label: 'Составляющие',
      hint: 'Показать линиями, сколько очков дала каждая часть оценки: цена, спот, контракты, интерес, объём, ликвидации, финансирование. В сумме они дают оценку.',
      type: 'boolean',
      default: false,
    },
    {
      key: 'table',
      label: 'Табло',
      hint: 'Короткая сводка словами в углу графика: что сейчас и насколько это заметно.',
      type: 'boolean',
      default: true,
    },
  ],
  outputs: [
    { key: 'score', label: 'Оценка', type: 'histogram', defaultColor: COLORS.up },
    ...PARTS.map((part, i) => ({
      key: part.key,
      label: part.label,
      type: 'line' as const,
      defaultColor: COLORS.parts[i]!,
      lineWidth: 1 as const,
    })),
  ],
  scale: {
    levels: [
      { value: BIAS_AT, color: COLORS.up, dashed: true },
      { value: 0, color: COLORS.grey, dashed: true },
      { value: -BIAS_AT, color: COLORS.down, dashed: true },
    ],
  },
  warmup: () => 0,
  label: () => 'Оценка Market Intelligence',
  calc: (candles, p, context) => {
    const points = scorePoints(candles, context);
    const result: Record<string, IndicatorPoint[]> = {
      score: points.map((point, i) => ({
        time: candles[i]!.time,
        value: point?.value ?? null,
        color:
          point === null
            ? COLORS.grey
            : point.value >= BIAS_AT
              ? COLORS.up
              : point.value <= -BIAS_AT
                ? COLORS.down
                : COLORS.grey,
      })),
    };
    const parts = bool(p, 'parts', false);
    for (const part of PARTS) {
      result[part.key] = parts
        ? points.map((point, i) => ({ time: candles[i]!.time, value: point?.parts?.[part.key] ?? null }))
        : candles.map((candle) => ({ time: candle.time, value: null }));
    }
    return result;
  },
  readout: (candles, p, context) => {
    if (!bool(p, 'table', true) || candles.length === 0) return [];
    if (!context.derivativesReady) return [{ label: 'Данные', value: 'едут…', tone: 'muted' }];
    const last = scorePoints(candles.slice(-1), context)[0];
    if (!last) {
      return [{ label: 'Данные', value: 'снимков по этой монете нет', tone: 'muted' }];
    }
    /*
     * Замер на истории (память: «оценка меряет напряжение, а не
     * сторону»): размах следующего движения оценка предсказывает, а его
     * направление — нет. Табло говорит об этом прямо, иначе «+40» читали
     * бы как «покупай».
     */
    const strong = Math.abs(last.value) >= 50;
    const situation: Situation =
      last.value >= BIAS_AT
        ? {
            title: strong ? 'Сильный перевес покупателей' : 'Перевес покупателей',
            tone: 'bull',
            text: 'Большинство признаков — цена, спот, контракты, интерес, объём — сейчас за покупателей. Оценка лучше предсказывает силу следующего хода, чем его сторону: направление подтверждайте ценой.',
          }
        : last.value <= -BIAS_AT
          ? {
              title: strong ? 'Сильный перевес продавцов' : 'Перевес продавцов',
              tone: 'bear',
              text: 'Большинство признаков — цена, спот, контракты, интерес, объём — сейчас за продавцов. Оценка лучше предсказывает силу следующего хода, чем его сторону: направление подтверждайте ценой.',
            }
          : { title: 'Без перевеса', tone: 'muted', text: 'Признаки спорят друг с другом — напряжения на рынке нет, сильного хода оценка не ждёт.' };
    const rows: ReadoutRow[] = [...situationRows(situation), { label: 'Оценка', value: signed(last.value), tone: last.value > 0 ? 'up' : last.value < 0 ? 'down' : 'muted' }];
    for (const part of PARTS) {
      const value = last.parts?.[part.key];
      if (value === undefined) continue;
      // Финансирование срабатывает редко; нулевая строка в табло — шум.
      if (part.key === 'funding' && value === 0) continue;
      rows.push({ label: part.label, value: signed(value), tone: value > 0.5 ? 'up' : value < -0.5 ? 'down' : 'muted' });
    }
    return rows;
  },
};

/* ---------------------------------------------------------- ликвидации */

/**
 * Ликвидации рынка: сколько позиций вынесло в каждой свече.
 *
 * Считается по всем биржам, которые их публикуют: Binance и OKX отдают
 * весь рынок, Bybit — главные монеты. Сумма Binance занижена: он
 * публикует не больше одной ликвидации в секунду на инструмент, и это
 * его ограничение, а не наша потеря.
 */
export const MARKET_LIQUIDATIONS: IndicatorDefinition = {
  id: 'market-liquidations',
  name: 'Ликвидации рынка',
  description:
    'Сколько длинных и коротких позиций биржи закрыли принудительно в каждой свече, в долларах. ' +
    'Скопления ликвидаций часто отмечают разворот или ускорение движения.',
  author: 'specter',
  placement: 'pane',
  derivatives: ['liqLong', 'liqShort', 'liqCount'],
  params: [
    {
      key: 'side',
      label: 'Показывать',
      hint: 'Обе стороны сразу — длинные вверх, короткие вниз; или только одну из них.',
      type: 'select',
      default: 'both',
      options: [
        { value: 'both', label: 'Длинные и короткие' },
        { value: 'long', label: 'Только длинные' },
        { value: 'short', label: 'Только короткие' },
      ],
    },
    {
      key: 'spike',
      label: 'Всплеск — во сколько раз больше обычного',
      hint: 'Для уведомлений: сообщать, когда за свечу вынесло во столько раз больше, чем в среднем за полсотни предыдущих. Выше — только крупные зачистки.',
      type: 'number',
      default: 3,
      min: 1.5,
      max: 20,
      step: 0.5,
    },
    {
      key: 'table',
      label: 'Табло',
      hint: 'Короткая сводка словами в углу графика: кого вынесло на последней свече.',
      type: 'boolean',
      default: true,
    },
  ],
  outputs: [
    { key: 'long', label: 'Вынесло длинных, $', type: 'histogram', defaultColor: COLORS.down, format: 'money', solid: true },
    { key: 'short', label: 'Вынесло коротких, $', type: 'histogram', defaultColor: COLORS.up, format: 'money', solid: true },
  ],
  scale: { levels: [{ value: 0, color: COLORS.grey, dashed: true }] },
  warmup: () => 0,
  label: () => 'Ликвидации рынка',
  calc: (candles, p, context) => {
    const side = typeof p['side'] === 'string' ? p['side'] : 'both';
    /*
     * Ликвидация — событие, а не состояние: на свече без них ноль, а не
     * пусто. Ряд приходит только по тем барам, где кого-то вынесло.
     */
    const bars = (kind: 'liqLong' | 'liqShort', shown: boolean): IndicatorPoint[] => {
      const points = context.derivative(kind);
      // Ряда нет вовсе — пусто: ноль означал бы «никого не вынесло», а
      // это другое утверждение, чем «мы не знаем».
      if (!shown || points.length === 0) return candles.map((candle) => ({ time: candle.time, value: null }));
      const byTime = new Map(points.map((point) => [point.at, point.value]));
      // Короткие вниз: так обе стороны читаются одним взглядом.
      const sign = kind === 'liqShort' && side === 'both' ? -1 : 1;
      return candles.map((candle) => ({ time: candle.time, value: (byTime.get(candle.time) ?? 0) * sign }));
    };
    return {
      long: bars('liqLong', side !== 'short'),
      short: bars('liqShort', side !== 'long'),
    };
  },
  readout: (candles, p, context) => {
    if (!bool(p, 'table', true) || candles.length === 0) return [];
    if (!context.derivativesReady) return [{ label: 'Данные', value: 'едут…', tone: 'muted' }];
    const last = candles.at(-1)!.time;
    const at = (kind: 'liqLong' | 'liqShort' | 'liqCount'): number =>
      context.derivative(kind).find((point) => point.at === last)?.value ?? 0;
    const long = at('liqLong');
    const short = at('liqShort');
    const count = at('liqCount');
    if (long + short <= 0) {
      return [{ label: 'На этой свече', value: 'никого не вынесло', tone: 'muted' }];
    }
    const share = Math.round((long / (long + short)) * 100);
    const situation: Situation =
      share >= 60
        ? {
            title: 'Выносит длинных',
            tone: 'bear',
            text: 'На этой свече принудительно закрывали в основном ставки на рост — это продажи по рынку, они ускоряют падение. После крупной волны часто бывает отскок.',
          }
        : share <= 40
          ? {
              title: 'Выносит коротких',
              tone: 'bull',
              text: 'На этой свече принудительно закрывали в основном ставки на падение — это покупки по рынку, они ускоряют рост. После крупной волны часто бывает откат.',
            }
          : { title: 'Выносит обе стороны', tone: 'muted', text: 'Позиции выносит в обе стороны поровну — рынок дёргает туда-сюда.' };
    const rows: ReadoutRow[] = [
      ...situationRows(situation),
      { label: 'Вынесло длинных', value: money(long), tone: long > 0 ? 'down' : 'muted' },
      { label: 'Вынесло коротких', value: money(short), tone: short > 0 ? 'up' : 'muted' },
      { label: 'Позиций', value: String(Math.round(count)) },
    ];
    // Binance отдаёт не больше одной ликвидации в секунду на инструмент.
    rows.push({ label: 'Точность', value: 'сумма Binance занижена биржей', tone: 'muted' });
    return rows;
  },
};

/** Деньги коротко: $1,2 млн вместо одиннадцати цифр. */
function money(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1e9) return `$${(abs / 1e9).toFixed(1).replace('.', ',')} млрд`;
  if (abs >= 1e6) return `$${(abs / 1e6).toFixed(1).replace('.', ',')} млн`;
  if (abs >= 1e3) return `$${Math.round(abs / 1e3)} тыс.`;
  return `$${Math.round(abs)}`;
}
